/**
 * Time-limited membership access, from the UI's side.
 *
 * The API stores `memberships.expires_at` as an instant and enforces
 * `expires_at > NOW()`. The dashboard picks that instant with an
 * `<input type="date">`, which speaks calendar days — so the whole job here is
 * turning one into the other without losing the day the user actually meant.
 *
 * No `window` access at module scope: this is imported by a node-environment
 * unit test.
 */

const YYYY_MM_DD = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The last millisecond of the given calendar day, in the viewer's own timezone.
 *
 * `<input type="date">` yields a bare `yyyy-mm-dd`, and `new Date("2026-08-21")`
 * parses that as UTC **midnight** — the instant the day begins, not ends. Sent as
 * an expiry that grants up to a day less access than was picked, and for "today"
 * it is an already-past instant the API rejects outright.
 *
 * Building from local calendar parts fixes both, and round-trips: rendering the
 * stored instant back through the same local calendar yields the date that was
 * picked. An end-of-day boundary in UTC would not — east of UTC it would read
 * back as the following day.
 *
 * Returns null for anything that is not a complete, real date.
 */
export function endOfLocalDay(yyyyMmDd: string): Date | null {
  const parts = YYYY_MM_DD.exec(yyyyMmDd);
  if (!parts) return null;
  const year = Number(parts[1]);
  const month = Number(parts[2]) - 1;
  const day = Number(parts[3]);
  const at = new Date(year, month, day, 23, 59, 59, 999);
  // The Date constructor rolls overflow forward (month 12 -> January), so a
  // nonsense date silently becomes a valid different one unless it is checked back.
  if (at.getFullYear() !== year || at.getMonth() !== month || at.getDate() !== day) return null;
  return at;
}

/**
 * The first instant of the given calendar day, in the viewer's own timezone.
 *
 * The `from` half of `endOfLocalDay`'s pair. A date-range filter has to bound
 * both ends in the same calendar the results are RENDERED in, or the filter and
 * the column disagree: the audit log built its bounds as `${day}T00:00:00Z` /
 * `${day}T23:59:59.999Z` while the When column rendered `toLocaleString()`, so
 * east of UTC a To of 26 Aug returned rows displaying 27 Aug.
 *
 * Returns null for anything that is not a complete, real date.
 */
export function startOfLocalDay(yyyyMmDd: string): Date | null {
  const parts = YYYY_MM_DD.exec(yyyyMmDd);
  if (!parts) return null;
  const year = Number(parts[1]);
  const month = Number(parts[2]) - 1;
  const day = Number(parts[3]);
  const at = new Date(year, month, day, 0, 0, 0, 0);
  if (at.getFullYear() !== year || at.getMonth() !== month || at.getDate() !== day) return null;
  return at;
}

/**
 * Today on the viewer's own calendar, as `yyyy-mm-dd` for an `<input type="date">`
 * `min`. `toISOString().slice(0, 10)` is today in **UTC**, which is already
 * tomorrow for anyone far enough east late in the day — as a `min` that silently
 * refuses the user's own today.
 */
export function todayLocalDate(now: Date = new Date()): string {
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/**
 * Whether a membership with this expiry still grants access right now.
 *
 * Mirrors `isMembershipUsable` in services/api: no expiry means no limit, and an
 * expiry that cannot be parsed is a corrupt grant rather than an unlimited one —
 * so the UI must not render it as "no limit" when the API treats it as dead.
 */
export function grantsAccess(expiresAt: string | null | undefined, now: number = Date.now()): boolean {
  if (expiresAt === null || expiresAt === undefined || expiresAt === "") return true;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return false;
  return at > now;
}

/**
 * Order two members by when their access ends, soonest first.
 *
 * "No expiry" is not an empty string, and the generic `String(a[f] ?? "")`
 * comparator every other column uses treats it as one — so ascending puts every
 * permanent member ahead of every dated row, and the click that means "who
 * lapses soonest" opens on a page of dashes. Undated rows sort last in BOTH
 * directions here: they are the absence of the thing being ranked, not its
 * smallest value.
 *
 * An unparseable expiry ranks first, matching `grantsAccess` — the row renders
 * as "Expired", so it belongs with the most urgent, not with the permanent.
 */
export function compareByExpiry(
  a: string | null | undefined,
  b: string | null | undefined,
  dir: "asc" | "desc" = "asc",
): number {
  const rank = (v: string | null | undefined): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const at = Date.parse(v);
    return Number.isNaN(at) ? -Infinity : at;
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra === null || rb === null) return ra === rb ? 0 : ra === null ? 1 : -1;
  if (ra === rb) return 0;
  return dir === "asc" ? (ra < rb ? -1 : 1) : (ra < rb ? 1 : -1);
}

/**
 * Order two rows by an instant, oldest first when ascending.
 *
 * The People table renders "Added" through `toLocaleDateString`, and the generic
 * `String(a[f] ?? "").localeCompare(...)` comparator every text column uses then
 * ranks that TEXT. Under en-GB the cell reads "18 May 2026", so alphabetical
 * order puts every 18th ahead of every 4th ahead of every 6th — the ascending
 * click, which the arrow promises means oldest-first, returned newest-first.
 *
 * Undated rows sort last in BOTH directions, as in `compareByExpiry`: they are
 * the absence of the value being ranked, not its smallest.
 */
export function compareByDate(
  a: string | null | undefined,
  b: string | null | undefined,
  dir: "asc" | "desc" = "asc",
): number {
  const rank = (v: string | null | undefined): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const at = Date.parse(v);
    return Number.isNaN(at) ? null : at;
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra === null || rb === null) return ra === rb ? 0 : ra === null ? 1 : -1;
  if (ra === rb) return 0;
  return dir === "asc" ? (ra < rb ? -1 : 1) : (ra < rb ? 1 : -1);
}
